import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppIcon from "@/components/AppIcon";
import { ArrowUpRight, ArrowDownRight, Sparkles, Info, ArrowRight, RefreshCw, Bell, Users } from "lucide-react";
import { toast } from "sonner";
import EducationalBlock from "@/components/EducationalBlock";

export default function Overview() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  // Gate summary loads: the initial projectId comes from localStorage and
  // may point at a project that was deleted or moved to another workspace.
  // We only start fetching summary/competitors AFTER /projects has confirmed
  // the id is still valid — hence `projectValidated`.
  const [projectValidated, setProjectValidated] = useState(false);
  const [summary, setSummary] = useState(null);
  const [projectId, setProjectId] = useState(() => localStorage.getItem("sn_project") || "");
  const [busy, setBusy] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [competitors, setCompetitors] = useState([]);

  const resetDashboard = () => {
    // A failure to load the workspace or the summary for the saved id means
    // the id is stale (project deleted, workspace revoked, etc.). Clear it
    // so the next mount picks the first available project cleanly.
    localStorage.removeItem("sn_project");
    setProjectId("");
    setSummary(null);
    setCompetitors([]);
    setProjectValidated(false);
  };

  useEffect(() => {
    api.get("/projects")
      .then((r) => {
        setProjects(r.data);
        const saved = localStorage.getItem("sn_project") || "";
        const inList = saved && r.data.some((p) => p.id === saved);
        if (inList) {
          if (projectId !== saved) setProjectId(saved);
          setProjectValidated(true);
        } else if (r.data.length) {
          const next = r.data[0].id;
          setProjectId(next);
          localStorage.setItem("sn_project", next);
          setProjectValidated(true);
        } else {
          // Workspace has no projects — nothing to validate, but drop any
          // stale saved id so future mounts don't hit ghost lookups.
          localStorage.removeItem("sn_project");
          setProjectId("");
          setProjectValidated(false);
        }
      })
      .catch(() => {
        setProjects([]);
        resetDashboard();
      });
    api.get("/alerts", { params: { unread_only: true, limit: 5 } }).then((r) => setAlerts(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    // Guard: don't hit /dashboard/summary until /projects has confirmed the
    // id is present in the current list. Firing early with a stale id from
    // localStorage returns 404 and looks like a broken dashboard.
    if (!projectId || !projectValidated) return;
    api.get("/dashboard/summary", { params: { project_id: projectId } })
      .then((r) => setSummary(r.data))
      .catch(() => {
        // The id passed our list-validation check but the summary endpoint
        // still 404'd — the project probably got deleted between the two
        // calls. Clear the saved value so the next reload picks a fresh one.
        resetDashboard();
      });
    api.get("/competitors/movement", { params: { project_id: projectId } })
      .then((r) => setCompetitors(r.data))
      .catch(() => setCompetitors([]));
  }, [projectId, projectValidated]);

  const switchProject = (id) => {
    setProjectId(id);
    localStorage.setItem("sn_project", id);
  };

  const refresh = async () => {
    if (!projectId) return;
    setBusy(true);
    try {
      const r = await api.post(`/keywords/${projectId}/refresh`);
      const s = await api.get("/dashboard/summary", { params: { project_id: projectId } });
      setSummary(s.data);
      if (r?.data?.mode === "priority-async") {
        const q = r.data.queued ?? 0;
        toast.success(`Refresh queued for ${q} keyword${q === 1 ? "" : "s"} — new ranks land in ~1 min.`);
      } else {
        toast.success("Rankings refreshed.");
      }
    } catch { toast.error("Refresh failed."); }
    finally { setBusy(false); }
  };

  if (projects.length === 0) {
    return (
      <div className="p-10 max-w-2xl">
        <AppIcon size={72} className="mb-6" />
        <h1 className="text-3xl font-display font-bold text-[#0A0D14]">Welcome to SerpNex.</h1>
        <p className="mt-3 text-[#525B6A]">Let's get you tracking in under two minutes. Create your first project.</p>
        <Link to="/app/projects" data-testid="overview-create-project" className="mt-6 btn-primary inline-flex px-5 py-2.5 rounded-md text-sm items-center gap-2">Create project <ArrowRight className="h-4 w-4" /></Link>
      </div>
    );
  }

  const s = summary?.changes;
  const t = summary?.totals;
  const p = summary?.project;
  const staleMinutes = p?.last_updated_at ? Math.floor((Date.now() - new Date(p.last_updated_at).getTime()) / 60000) : null;

  return (
    <div className="p-8 lg:p-10">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">Today's Executive Summary</div>
          <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">Good morning.</h1>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="project-selector" value={projectId} onChange={(e) => switchProject(e.target.value)} className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
            {projects.map((pr) => <option key={pr.id} value={pr.id}>{pr.name} · {pr.domain}</option>)}
          </select>
          <button onClick={refresh} disabled={busy} data-testid="overview-refresh" className="btn-ghost px-3 py-2 rounded-md text-sm inline-flex items-center gap-1">
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </header>

      {/* Transparency banner */}
      <div className={`mt-6 rounded-lg border p-4 flex items-start gap-3 ${staleMinutes !== null && staleMinutes < 60 ? 'border-emerald-200 bg-emerald-50' : staleMinutes > 1440 ? 'border-amber-200 bg-amber-50' : 'border-[#E2E8F0] bg-[#F8F9FA]'}`} data-testid="transparency-banner">
        <Info className={`h-4 w-4 mt-0.5 ${staleMinutes < 60 ? 'text-emerald-700' : staleMinutes > 1440 ? 'text-amber-700' : 'text-[#525B6A]'}`} />
        <div className="text-sm">
          <div className="font-medium text-[#0A0D14]">
            {staleMinutes === null ? "No refresh yet" : staleMinutes < 60 ? "Data is fresh" : staleMinutes < 1440 ? "Data is up-to-date" : "Data may be delayed"}
          </div>
          <div className="text-[#525B6A]">
            Last updated: {p?.last_updated_at ? new Date(p.last_updated_at).toLocaleString() : "—"} · Daily update cadence · Refresh manually anytime.
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-6 gap-3">
        <StatCard label="Tracked" value={t?.tracked ?? 0} />
        <StatCard label="Ranked" value={t?.ranked ?? 0} />
        <StatCard label="Top 10" value={t?.top10 ?? 0} accent />
        <StatCard label="Top 3" value={t?.top3 ?? 0} accent />
        <StatCard label="Improved" value={s?.improved ?? 0} tone="success" />
        <StatCard label="Dropped" value={s?.dropped ?? 0} tone="danger" />
      </div>

      <div className="mt-6">
        <div className="card-crisp p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Estimated Click Gain</div>
              <div className="mt-1 stat-value text-4xl font-bold text-[#0047FF]">
                {(s?.net_click_impact ?? 0) >= 0 ? '+' : ''}{s?.net_click_impact ?? 0}
              </div>
              <div className="text-xs text-[#8A94A6] mt-1">Net expected daily traffic change from ranking movement</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-emerald-600 tabular-nums">+{s?.estimated_click_gain ?? 0} <span className="text-xs text-[#8A94A6]">gained</span></div>
              <div className="text-sm text-red-600 tabular-nums">−{s?.estimated_click_loss ?? 0} <span className="text-xs text-[#8A94A6]">lost</span></div>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <MiniStat label="Entered top 3" value={s?.entered_top3 ?? 0} tone="success" />
            <MiniStat label="Entered top 10" value={s?.entered_top10 ?? 0} tone="success" />
          </div>
        </div>

        {/* Temporarily hidden — "Today's priority" card is being redesigned.
            Restore by re-wrapping this and the click-gain block in a
            `lg:grid-cols-3` grid and adding `lg:col-span-2` back on the
            click-gain card above. */}
        {/*
        <div className="card-crisp p-6">
          <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Today's priority</div>
          ...
        </div>
        */}
      </div>

      <div className="mt-6 flex items-center justify-between border-t border-[#E2E8F0] pt-6">
        <Link to={`/app/projects/${projectId}`} data-testid="overview-view-project" className="text-sm text-[#0047FF] inline-flex items-center gap-1">
          Open project → full keyword table <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <div className="text-xs text-[#8A94A6]">Daily estimated clicks: <span className="tabular-nums font-medium text-[#0A0D14]">{t?.estimated_daily_clicks ?? 0}</span></div>
      </div>

      {/* Alerts (competitor movement panel temporarily hidden — being redesigned) */}
      <div className="mt-8">
        <div className="card-crisp p-6" data-testid="alerts-panel">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#8A94A6] font-medium">
              <Bell className="h-3.5 w-3.5" /> Recent alerts
            </div>
            <Link to="/app/alerts" className="text-xs text-[#0047FF]">See all →</Link>
          </div>
          <div className="mt-4 space-y-2">
            {alerts.length === 0 && <div className="text-sm text-[#8A94A6] py-6 text-center">No unread alerts. Silence is good.</div>}
            {alerts.map((a) => (
              <div key={a.id} className="flex items-start gap-2 p-2 rounded-md hover:bg-[#F8F9FA]">
                <span className={`h-1.5 w-1.5 rounded-full mt-2 ${a.type === 'major_drop' || a.type === 'competitor_overtake' ? 'bg-red-500' : 'bg-emerald-500'}`} />
                <div className="flex-1 text-sm text-[#0A0D14]">{a.payload?.summary}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Temporarily hidden — "Competitor movement" panel is being redesigned.
            Restore by wrapping both panels in `lg:grid-cols-2 gap-6` again. */}
        {/*
        <div className="card-crisp p-6" data-testid="competitors-panel">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#8A94A6] font-medium">
              <Users className="h-3.5 w-3.5" /> Competitor movement
            </div>
            ...
          </div>
        </div>
        */}
      </div>

      {/* Contextual education — temporarily hidden per product request */}
      {/*
      {(() => {
        const key = alerts.some((a) => a.type === 'competitor_overtake') ? 'competitor_overtake'
          : (s?.dropped ?? 0) >= 3 ? 'post_google_update'
          : (t?.top10 ?? 0) > 0 && (t?.top3 ?? 0) === 0 ? 'low_ctr_opportunity'
          : null;
        return key ? <EducationalBlock lessonKey={key} className="mt-6" /> : null;
      })()}
      */}
    </div>
  );
}

function StatCard({ label, value, tone, accent }) {
  const color = tone === 'success' ? 'text-emerald-600' : tone === 'danger' ? 'text-red-600' : accent ? 'text-[#0047FF]' : 'text-[#0A0D14]';
  return (
    <div className="card-crisp p-4" data-testid={`stat-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium">{label}</div>
      <div className={`stat-value text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const color = tone === 'success' ? 'text-emerald-600' : 'text-red-600';
  const icon = tone === 'success' ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />;
  return (
    <div className="border border-[#E2E8F0] rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium">{label}</div>
      <div className={`stat-value text-xl font-bold mt-1 ${color} inline-flex items-center gap-1`}>{icon} {value}</div>
    </div>
  );
}
